/**
 * Laying overlapping events side by side in a day column.
 *
 * Two events at the same hour cannot both have the column, so they split it.
 * The rule is the one calendars have used for decades:
 *
 *   1. Gather events into *clusters* — runs of events connected by overlap.
 *      A and C may not touch each other, but if both touch B, all three are
 *      one cluster and all three narrow together. Otherwise C would sit on
 *      top of A.
 *   2. Inside a cluster, give each event the first *lane* that is free at its
 *      start time.
 *   3. Every event in the cluster is `1 / lanes` wide, so the column stays
 *      aligned rather than ragged.
 */

export interface Span {
  /** Minutes from the top of the grid. */
  start: number;
  /** Minutes from the top. Always greater than start — see MIN_MINUTES. */
  end: number;
}

export interface Placed<T> {
  item: T;
  lane: number;
  /** How many lanes the whole cluster needs. */
  lanes: number;
}

/**
 * An event with no end still needs to be visible and to take part in overlap.
 * The design gives it a 28px minimum, which at 58px an hour is ~29 minutes.
 */
export const MIN_MINUTES = 29;

export function layOut<T>(items: T[], spanOf: (item: T) => Span): Placed<T>[] {
  const spans = items
    .map((item) => {
      const span = spanOf(item);
      return { item, start: span.start, end: Math.max(span.end, span.start + MIN_MINUTES) };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const placed: Placed<T>[] = [];

  let cluster: typeof spans = [];
  let clusterEnd = -Infinity;

  const flush = (): void => {
    if (cluster.length === 0) return;

    // Greedy lanes: the first lane whose previous event has finished.
    const laneEnds: number[] = [];
    const assignments = cluster.map((entry) => {
      let lane = laneEnds.findIndex((end) => end <= entry.start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(entry.end);
      } else {
        laneEnds[lane] = entry.end;
      }
      return { entry, lane };
    });

    for (const { entry, lane } of assignments) {
      placed.push({ item: entry.item, lane, lanes: laneEnds.length });
    }

    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const entry of spans) {
    // A gap means the previous cluster is closed. Touching end-to-start is a
    // gap, not an overlap — back-to-back events each keep the full column.
    if (entry.start >= clusterEnd) flush();
    cluster.push(entry);
    clusterEnd = Math.max(clusterEnd, entry.end);
  }

  flush();

  return placed;
}
